import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import store from '.'
import { Context } from '@/orrm/model/context'
import ContextWrapper from '@/orrm/wrapper/context'
import { PluralResponse } from 'coloquent'
import cloneDeep from 'lodash/cloneDeep'
import { MapSingleton } from '@/utils/map'
import { Layer } from '@/orrm/model/layer'
import LayerWrapper from '@/orrm/wrapper/layer'

const _namespace = 'context'

@Module({ dynamic: true, store, namespaced: true, name: _namespace })
export default class ContextModule extends VuexModule {
  sortAsc = false
  loadingContexts = false
  contexts: ContextWrapper[] = []
  originalContexts: ContextWrapper[] = []
  activeContext: ContextWrapper | null = null
  defaultLayers: Layer[] = []

  get hasActiveContext (): boolean {
    if (this.activeContext) {
      return this.activeContext.model.uuid !== Context.context0uuid
    }
    return false
  }

  @Mutation
  setSortAsc (value: boolean) {
    this.sortAsc = value
  }

  @Mutation
  setActiveContext (value: ContextWrapper | null) {
    if (this.activeContext && MapSingleton.map) {
      const mapLayers = MapSingleton.map.getLayers().getArray()
      for (const layer of this.activeContext.layers) {
        if (mapLayers.indexOf(layer.olLayer) !== -1) {
          MapSingleton.map.removeLayer(layer.olLayer)
        }
      }
    }

    if (value === null) {
      const context0 = this.contexts.find((context: ContextWrapper) => context.model.uuid === Context.context0uuid)
      if (context0) {
        this.activeContext = context0
      }
    } else {
      this.activeContext = value
    }
  }

  @Mutation
  setLoadingContexts (loadingContexts: boolean) {
    this.loadingContexts = loadingContexts
  }

  @Mutation
  resetOriginalContexts () {
    const context0 = Context.createContext0()

    this.originalContexts = [new ContextWrapper(context0)]
  }

  @Mutation
  resetContext (context: ContextWrapper) {
    for (const originalContext of this.originalContexts) {
      if (context.model.uuid === originalContext.model.uuid) {
        const index = this.contexts.indexOf(context)
        if (index !== -1) {
          this.contexts[index] = cloneDeep(originalContext)
          if (this.activeContext && this.contexts[index].model.uuid === this.activeContext.model.uuid) {
            this.activeContext = this.contexts[index]
          }
        } else {
          throw new Error('Impossible de réinitialiser le contexte (contexte introuvable)')
        }
        return
      }
    }
  }

  @Mutation
  private setContexts (contexts: ContextWrapper[]) {
    this.contexts = contexts
  }

  @Action({ rawError: true })
  async fetchAllContexts () {
    let missing = true
    let currentPage = 0
    this.resetOriginalContexts()

    const defaultLayers = (await Layer.getQueryBuilder()
      .where('field_orrm_affichage_defaut', '1')
      .get(0)).getData()

    this.originalContexts[0].layers.push(...defaultLayers.map(l => new LayerWrapper(l)))

    do {
      try {
        this.setLoadingContexts(true)
        const res = await Context
          .getQueryBuilder()
          // .where('field_orrm_couches_carto.field_orrm_afficher_la_couche', '1')
          .get(currentPage)

        const data = res.getData() as Context[]
        if (data) {
          this.setLoadingContexts(false)
          const result = Array.isArray(data) ? data : [data]
          if (result.length) {
            this.originalContexts.push(...result.map((context: Context) => new ContextWrapper(context)))
          } else {
            missing = false
          }
        }
        currentPage = (res as PluralResponse).getPageNumber() + 1
      } catch (e) {
        console.error(e)
        this.setLoadingContexts(false)
        break
      }
    } while (missing)
    this.setContexts(this.originalContexts.map((context: ContextWrapper) => cloneDeep(context)))
  }
}
